import {Mapper} from '@s-ui/domain'

export class VehicleStructuredDataSeoMapper extends Mapper {
  #translations = {
    'es-ES': {
      fuelType: {
        Eléctrico: 'Electric',
        Híbrido: 'Hybrid',
        Diésel: 'Diesel',
        Gasolina: 'Gasoline',
        GLP: 'LPG',
        GNC: 'CNG'
      },
      transmission: {
        Automático: 'Automatic',
        Manual: 'Manual'
      }
    }
  }

  /**
   * @constructor
   * @param {Object} deps
   * @param {Object} deps.config
   * @param {Function} deps.structuredDataValueObjectFactory
   * */
  constructor({config, structuredDataValueObjectFactory}) {
    super()
    this._config = config
    this._structuredDataValueObjectFactory = structuredDataValueObjectFactory
    this._defaultCulture = this._config.get('DEFAULT_CULTURE')
  }

  /**
   * Normalizes fuel type based on current culture
   * @private
   * @param {string} fuelType - Fuel type value
   * @return {string|null} - Normalized fuel type or null
   */
  #normalizeFuelType = fuelType => {
    if (!fuelType) return null

    const cultureMap = this.#translations[this._defaultCulture]
    if (!cultureMap || !cultureMap.fuelType) return fuelType

    return cultureMap.fuelType[fuelType] || fuelType
  }

  /**
   * Normalizes transmission type based on current culture
   * @private
   * @param {string} transmission - Transmission type
   * @return {string|null} - Normalized transmission type or null
   */
  #normalizeTransmission = transmission => {
    if (!transmission) return null

    const cultureMap = this.#translations[this._defaultCulture]
    if (!cultureMap || !cultureMap.transmission) return transmission

    return cultureMap.transmission[transmission] || transmission
  }

  /**
   * Creates additionalProperty array for non-standard properties
   * @private
   * @param {string|null} environmentalLabel - Environmental label value
   * @return {Array|null} - Array of PropertyValue objects or null
   */
  #buildAdditionalProperties = environmentalLabel => {
    if (!environmentalLabel) return null

    const environmentalLabelName = {
      'es-ES': 'Etiqueta Ambiental'
    }
    return [
      {
        '@type': 'PropertyValue',
        name: environmentalLabelName[this._defaultCulture],
        value: environmentalLabel
      }
    ]
  }

  /**
   * Builds mileage object for odometer reading
   * @private
   * @param {number|null} maxKms - Maximum kilometers value
   * @return {Object|null} - QuantitativeValue object or null
   */
  #buildMileageFromOdometer = maxKms => {
    if (!maxKms) return null

    return {
      '@type': 'QuantitativeValue',
      maxValue: maxKms,
      unitCode: 'KMT'
    }
  }

  /**
   * https://schema.org/Product + https://schema.org/Car
   * @return {StructuredDataValueObject}
   */
  map = ({
    name = '',
    make,
    model,
    lowPrice,
    highPrice,
    canonical,
    fuelType,
    nResults,
    transmission,
    bodyType,
    environmentalLabel,
    maxKms,
    areaServed,
    offerTypeId
  }) => {
    const parseOfferCount = nResults => {
      if (typeof nResults === 'number') return nResults
      if (typeof nResults === 'string') {
        return parseInt(nResults.replace(/\./g, ''), 10)
      }
      return undefined
    }

    const parsePrice = price => {
      if (typeof price === 'number') return price
      if (typeof price === 'string') {
        return parseInt(price, 10)
      }
      return undefined
    }

    const parsedOfferCount = parseOfferCount(nResults)
    const parsedLowPrice = parsePrice(lowPrice)
    const parsedHighPrice = parsePrice(highPrice)

    const hasLowPrice = parsedLowPrice != null
    const hasHighPrice = parsedHighPrice != null
    const hasOfferCount = parsedOfferCount != null
    const hasOffer = hasLowPrice || hasHighPrice || hasOfferCount

    const offers = hasOffer
      ? {
          '@type': 'AggregateOffer',
          ...(hasLowPrice && {lowPrice: parsedLowPrice}),
          ...(hasHighPrice && {highPrice: parsedHighPrice}),
          ...(hasOfferCount && {offerCount: parsedOfferCount}),
          priceCurrency: 'EUR',
          ...(areaServed && {
            areaServed: {
              '@type': 'AdministrativeArea',
              name: areaServed
            }
          })
        }
      : null

    const {OFFER_TYPE_NEW, OFFER_TYPE_KM0} = this._config.get('search')
    const itemCondition =
      offerTypeId === OFFER_TYPE_NEW || offerTypeId === OFFER_TYPE_KM0
        ? 'https://schema.org/NewCondition'
        : 'https://schema.org/UsedCondition'

    const {STRUCTURED_DATA_VEHICLE_TYPE} = this._config.get('seo')

    const vehicleSchema = {
      '@context': 'http://schema.org',
      '@type': ['Product', STRUCTURED_DATA_VEHICLE_TYPE],
      ...(canonical && {'@id': `${canonical}#product`}),
      name,
      ...(make && {brand: {'@type': 'Brand', name: make}}),
      ...(model && {model}),
      itemCondition,
      ...(fuelType && {fuelType: this.#normalizeFuelType(fuelType)}),
      ...(transmission && {vehicleTransmission: this.#normalizeTransmission(transmission)}),
      ...(bodyType && {bodyType}),
      ...(maxKms && {mileageFromOdometer: this.#buildMileageFromOdometer(maxKms)}),
      ...(offers && {offers}),
      ...(environmentalLabel && {additionalProperty: this.#buildAdditionalProperties(environmentalLabel)})
    }

    return this._structuredDataValueObjectFactory(vehicleSchema)
  }
}
